import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const metricSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export type MetricResponse = z.infer<typeof metricSchema>;

type RosteringMetricType =
  | 'studentCount'
  | 'teacherCount'
  | 'classCount'
  | 'activeStudents'
  | 'activeTeachers';

type DatefulMetricType =
  | 'sessionDuration'
  | 'sessionCount'
  | 'resourcesViewed'
  | 'activeStudents'
  | 'activeTeachers'
  | 'studentsActivitiesCompleted'
  | 'engagedMinutes'
  | 'mediaPlayed'
  | 'textViewed'
  | 'standardsAccessed'
  | 'skillsAccessed'
  | 'mostAccessedStandard';

interface BaseProps {
  orgIds: string[];
  products: string[];
  contentId?: string;
  activityId?: string;
  enabled?: boolean;
  startDate?: Date;
  endDate?: Date;
}

interface DemographicIds {
  teacherIds: string[];
  classId?: string;
  studentId?: string;
}

interface Dates {
  startDate: Date;
  endDate: Date;
}

interface AllMetricProps
  extends BaseProps,
    Partial<DemographicIds>,
    Partial<Dates> {
  metricType: string;
}

type DemographicProps<T> = T extends { metricType: RosteringMetricType }
  ? T & Partial<DemographicIds>
  : T & DemographicIds;

type DatefulProps<T> = T extends { metricType: DatefulMetricType }
  ? T & Dates
  : T;

export type MetricTypeProps<S extends string> = DatefulProps<
  DemographicProps<
    BaseProps & {
      metricType: S;
    }
  >
>;

export type MetricApiProps =
  | MetricTypeProps<'sessionCount'>
  | MetricTypeProps<'sessionDuration'>
  | MetricTypeProps<'classCount'>
  | MetricTypeProps<'resourcesViewed'>
  | MetricTypeProps<'studentsViewed'>
  | MetricTypeProps<'studentCount'>
  | MetricTypeProps<'teacherCount'>
  | MetricTypeProps<'activeStudents'>
  | MetricTypeProps<'activeTeachers'>
  | MetricTypeProps<'studentsReacted'>
  | MetricTypeProps<'studentsActivityCompleted'>
  | MetricTypeProps<'avgQuizScore'>
  | MetricTypeProps<'studentQuizScore'>
  | MetricTypeProps<'quizzesCompleted'>
  | MetricTypeProps<'questionsAnswered'>
  | MetricTypeProps<'standardsPracticed'>
  | MetricTypeProps<'skillsPracticed'>
  | MetricTypeProps<'shortWritesCount'>
  | MetricTypeProps<'exitTicketsCount'>
  | MetricTypeProps<'annotationsCount'>
  | MetricTypeProps<'pollsCount'>
  | MetricTypeProps<'anticipationGuidesCount'>
  | MetricTypeProps<'studentsActivitiesCompleted'>
  | MetricTypeProps<'engagedMinutes'>
  | MetricTypeProps<'mediaPlayed'>
  | MetricTypeProps<'textViewed'>
  | MetricTypeProps<'standardsAccessed'>
  | MetricTypeProps<'skillsAccessed'>
  | MetricTypeProps<'mostAccessedStandard'>
  | MetricTypeProps<'orgCount'>
  | MetricTypeProps<'studentsLoggedIn'>
  | MetricTypeProps<'studentsEntitled'>;

export type MetricType = MetricApiProps['metricType'];

const getMetricsApi = async (props: MetricApiProps) => {
  const {
    metricType,
    classId,
    teacherIds,
    orgIds,
    startDate,
    endDate,
    products,
    contentId,
    studentId,
    activityId,
  } = props as AllMetricProps;

  return fetcher('/metrics', {
    queryParameters: {
      metricType,
      classId,
      teacherId: teacherIds?.join(','),
      orgId: orgIds.join(','),
      startDate: startDate ? formatDate(startDate) : undefined,
      endDate: endDate ? formatDate(endDate, { boundary: 'end' }) : undefined,
      products: products?.join(','),
      contentId,
      studentId,
      activityId,
    },
  }).then((res) => metricSchema.parse(res));
};

export function useMetricsApi(
  props: MetricApiProps
): UseQueryResult<MetricResponse, Error> {
  const {
    metricType,
    classId,
    teacherIds,
    orgIds,
    startDate,
    endDate,
    products,
    contentId,
    studentId,
    activityId,
    enabled,
  } = props as AllMetricProps;

  return useQuery({
    queryKey: [
      'metrics',
      metricType,
      classId,
      teacherIds,
      orgIds,
      formatDate(startDate),
      formatDate(endDate, { boundary: 'end' }),
      products,
      contentId,
      studentId,
      activityId,
    ].filter(Boolean),
    queryFn: () => getMetricsApi(props),
    enabled: enabled !== false,
  });
}
